import React, { useState } from 'react';
import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { FileBarChart, Loader2, Calendar, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { UserProfile } from '../types';

interface GenerateReportTriggerProps {
  className?: string;
  targetUserId?: string;
  allUsers?: UserProfile[];
}

export const GenerateReportTrigger: React.FC<GenerateReportTriggerProps> = ({ 
  className,
  targetUserId,
  allUsers
}) => {
  const { profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [periodType, setPeriodType] = useState<'daily' | 'weekly' | 'monthly' | 'annual' | 'custom'>('monthly');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const handleGenerate = async () => {
    if (!profile) {
      toast.error('You must be logged in to generate reports.');
      return;
    }

    const reportUser = allUsers && targetUserId
      ? allUsers.find(u => u.uid === targetUserId)
      : profile;

    if (!reportUser) {
      toast.error('Could not find the target user to generate report.');
      return;
    }

    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (periodType === 'daily') {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else if (periodType === 'weekly') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(now.setDate(diff));
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (periodType === 'monthly') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (periodType === 'annual') {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31);
      end.setHours(23, 59, 59, 999);
    } else if (periodType === 'custom') {
      if (!customStartDate || !customEndDate) {
        toast.error('Please specify both start and end dates.');
        return;
      }
      start = new Date(customStartDate);
      start.setHours(0, 0, 0, 0);
      end = new Date(customEndDate);
      end.setHours(23, 59, 59, 999);
    }

    setLoading(true);
    try {
      const parseDate = (val: any): Date | null => {
        if (!val) return null;
        if (typeof val.toDate === 'function') {
          return val.toDate();
        }
        if (val instanceof Date) {
          return val;
        }
        if (typeof val === 'string' || typeof val === 'number') {
          const d = new Date(val);
          if (!isNaN(d.getTime())) {
            return d;
          }
        }
        return null;
      };

      const fetchedTasksMap = new Map<string, any>();

      // We perform individual try-catch blocks inside Promise.all so that any permission restrictions
      // on certain collections (for lower-privileged users) will be gracefully bypassed without failing the whole operation.
      const fetchPromises = [
        // 1. Regular/Technical Tasks (Assigned to user as technician)
        getDocs(query(collection(db, 'tasks'), where('technicianId', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.dueDate) || parseDate(data.updatedAt) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Technical Task`, {
                  id: docSnap.id,
                  title: data.title || 'Untitled Task',
                  clientName: data.clientName || 'N/A',
                  status: data.status || 'N/A',
                  createdAt: created.toISOString(),
                  type: 'Technical Task',
                  description: data.description || ''
                });
              }
            });
          }).catch(err => console.warn('Skipped tasks query:', err)),

        // 2. Customer Care Tasks (Created/logged by user)
        getDocs(query(collection(db, 'customer_care_tasks'), where('createdBy', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.date) || parseDate(data.updatedAt) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Customer Care Task`, {
                  id: docSnap.id,
                  title: data.task || 'Untitled Task',
                  clientName: data.clientName || 'N/A',
                  status: data.status || 'N/A',
                  createdAt: created.toISOString(),
                  type: 'Customer Care Task',
                  description: data.comment || ''
                });
              }
            });
          }).catch(err => console.warn('Skipped customer care tasks query:', err)),

        // 3. Technical Service Reports (Created by user as technician)
        getDocs(query(collection(db, 'reports'), where('technicianId', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.updatedAt) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Technical Service Report`, {
                  id: docSnap.id,
                  title: `${data.type ? data.type.toUpperCase() : 'General'} Service Report`,
                  clientName: data.clientName || 'N/A',
                  status: data.status || 'N/A',
                  createdAt: created.toISOString(),
                  type: 'Technical Service Report',
                  description: data.findings || data.comments || ''
                });
              }
            });
          }).catch(err => console.warn('Skipped technical reports query:', err)),

        // 4. Customer Care Technical Reports (Created by user)
        getDocs(query(collection(db, 'customer_care_technical_reports'), where('createdBy', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.updatedAt) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Customer Care Tech Report`, {
                  id: docSnap.id,
                  title: data.reportNumber || `${data.type ? data.type.toUpperCase() : 'Service'} Technical Report`,
                  clientName: data.clientName || 'N/A',
                  status: 'Completed',
                  createdAt: created.toISOString(),
                  type: 'Customer Care Tech Report',
                  description: data.remedialRecommendations || ''
                });
              }
            });
          }).catch(err => console.warn('Skipped customer care technical reports query:', err)),

        // 5. Billing Invoices (Created by billing / account user)
        getDocs(query(collection(db, 'invoices'), where('creatorId', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.dueDate) || parseDate(data.updatedAt) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Billing Invoice`, {
                  id: docSnap.id,
                  title: `${data.type ? data.type.toUpperCase() : 'General'} Invoice ${data.invoiceNumber || 'Draft'}`,
                  clientName: data.clientName || 'N/A',
                  status: data.status || 'N/A',
                  createdAt: created.toISOString(),
                  type: 'Billing Invoice',
                  description: `Created invoice for ${data.clientName || 'client'}. Total: ${data.totalAmount || 0}`
                });
              }
            });
          }).catch(err => console.warn('Skipped invoices query:', err)),

        // 6. Stock Transactions (Created by store user)
        getDocs(query(collection(db, 'stock_transactions'), where('performedBy', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.date) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Inventory Transaction`, {
                  id: docSnap.id,
                  title: `Stock ${data.type ? data.type.toUpperCase() : 'Movement'}`,
                  clientName: data.itemName || 'N/A',
                  status: 'Completed',
                  createdAt: created.toISOString(),
                  type: 'Inventory Transaction',
                  description: `Moved ${data.quantity || 0} of ${data.itemName || 'item'}. Reason: ${data.reason || 'N/A'}`
                });
              }
            });
          }).catch(err => console.warn('Skipped stock transactions query:', err)),

        // 7. Cash Requisitions (Created by requester)
        getDocs(query(collection(db, 'cash_requisitions'), where('requestedById', '==', reportUser.uid)))
          .then(snap => {
            snap.forEach(docSnap => {
              const data = docSnap.data();
              const created = parseDate(data.createdAt) || parseDate(data.requisitionDate) || new Date();
              if (created >= start && created <= end) {
                fetchedTasksMap.set(`${docSnap.id}-Cash Requisition`, {
                  id: docSnap.id,
                  title: `Requisition ${data.codeNo || 'Draft'}`,
                  clientName: data.siteName || 'N/A',
                  status: data.status || 'N/A',
                  createdAt: created.toISOString(),
                  type: 'Cash Requisition',
                  description: `Requisition for ${data.siteName || 'site'}. Total: ${data.totalAmount || 0}`
                });
              }
            });
          }).catch(err => console.warn('Skipped cash requisitions query:', err)),

        // EXTRA INVOICE QUERIES (By Sales Agent & Creator Name to ensure all tasks by Jackson are captured)
        ...(reportUser.displayName ? [
          // Billing Invoices by salesAgent (Original Case)
          getDocs(query(collection(db, 'invoices'), where('salesAgent', '==', reportUser.displayName)))
            .then(snap => {
              snap.forEach(docSnap => {
                const data = docSnap.data();
                const created = parseDate(data.createdAt) || parseDate(data.dueDate) || parseDate(data.updatedAt) || new Date();
                if (created >= start && created <= end) {
                  fetchedTasksMap.set(`${docSnap.id}-Billing Invoice`, {
                    id: docSnap.id,
                    title: `${data.type ? data.type.toUpperCase() : 'General'} Invoice ${data.invoiceNumber || 'Draft'}`,
                    clientName: data.clientName || 'N/A',
                    status: data.status || 'N/A',
                    createdAt: created.toISOString(),
                    type: 'Billing Invoice',
                    description: `Created invoice for ${data.clientName || 'client'}. Total: ${data.totalAmount || 0}`
                  });
                }
              });
            }).catch(err => console.warn('Skipped invoices salesAgent query:', err)),

          // Billing Invoices by salesAgent (UPPERCASE)
          getDocs(query(collection(db, 'invoices'), where('salesAgent', '==', reportUser.displayName.toUpperCase())))
            .then(snap => {
              snap.forEach(docSnap => {
                const data = docSnap.data();
                const created = parseDate(data.createdAt) || parseDate(data.dueDate) || parseDate(data.updatedAt) || new Date();
                if (created >= start && created <= end) {
                  fetchedTasksMap.set(`${docSnap.id}-Billing Invoice`, {
                    id: docSnap.id,
                    title: `${data.type ? data.type.toUpperCase() : 'General'} Invoice ${data.invoiceNumber || 'Draft'}`,
                    clientName: data.clientName || 'N/A',
                    status: data.status || 'N/A',
                    createdAt: created.toISOString(),
                    type: 'Billing Invoice',
                    description: `Created invoice for ${data.clientName || 'client'}. Total: ${data.totalAmount || 0}`
                  });
                }
              });
            }).catch(err => console.warn('Skipped invoices salesAgent uppercase query:', err)),

          // Billing Invoices by creatorName
          getDocs(query(collection(db, 'invoices'), where('creatorName', '==', reportUser.displayName)))
            .then(snap => {
              snap.forEach(docSnap => {
                const data = docSnap.data();
                const created = parseDate(data.createdAt) || parseDate(data.dueDate) || parseDate(data.updatedAt) || new Date();
                if (created >= start && created <= end) {
                  fetchedTasksMap.set(`${docSnap.id}-Billing Invoice`, {
                    id: docSnap.id,
                    title: `${data.type ? data.type.toUpperCase() : 'General'} Invoice ${data.invoiceNumber || 'Draft'}`,
                    clientName: data.clientName || 'N/A',
                    status: data.status || 'N/A',
                    createdAt: created.toISOString(),
                    type: 'Billing Invoice',
                    description: `Created invoice for ${data.clientName || 'client'}. Total: ${data.totalAmount || 0}`
                  });
                }
              });
            }).catch(err => console.warn('Skipped invoices creatorName query:', err))
        ] : [])
      ];

      await Promise.all(fetchPromises);

      const fetchedTasks = Array.from(fetchedTasksMap.values());

      // Write to 'user_task_reports' collection
      await addDoc(collection(db, 'user_task_reports'), {
        userId: reportUser.uid,
        userName: reportUser.displayName || reportUser.email || 'Unknown User',
        userEmail: reportUser.email || '',
        periodType,
        startDate: Timestamp.fromDate(start),
        endDate: Timestamp.fromDate(end),
        tasksCount: fetchedTasks.length,
        tasks: fetchedTasks,
        createdAt: Timestamp.now(),
        generatedBy: profile.uid,
        generatedByName: profile.displayName || profile.email || 'Unknown User'
      });

      toast.success(`Successfully generated report with ${fetchedTasks.length} tasks! You can find it in the Reports module.`);
      setIsOpen(false);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report.');
      handleFirestoreError(error, OperationType.CREATE, 'user_task_reports');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={className || "flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-orange-200 text-sm cursor-pointer"}
        id="generate-task-report-btn"
      >
        <FileBarChart className="w-5 h-5" />
        Generate Report
      </button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !loading && setIsOpen(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 overflow-hidden border border-slate-100"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Generate Tasks Report</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Generate a report of your performed tasks</p>
                </div>
                {!loading && (
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Select Period</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['daily', 'weekly', 'monthly', 'annual', 'custom'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        disabled={loading}
                        onClick={() => setPeriodType(type)}
                        className={`px-4 py-2.5 rounded-xl text-xs font-bold capitalize transition-all border ${
                          periodType === type
                            ? 'bg-orange-500 text-white border-orange-500 shadow-sm shadow-orange-500/20'
                            : 'bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {periodType === 'custom' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3 pt-2"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Start Date</label>
                        <div className="relative">
                          <input
                            type="date"
                            disabled={loading}
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">End Date</label>
                        <div className="relative">
                          <input
                            type="date"
                            disabled={loading}
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="flex w-full gap-3 pt-4">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => setIsOpen(false)}
                    className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleGenerate}
                    className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-bold text-xs uppercase tracking-wider transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Now'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
